import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import approvalWorkflowService from '../../services/approvalWorkflowService';
import ApprovalChainCard from './components/ApprovalChainCard';
import ApprovalStepsList from './components/ApprovalStepsList';
import RoutingRulesTable from './components/RoutingRulesTable';
import EscalationRulesTable from './components/EscalationRulesTable';
import PendingApprovalCard from './components/PendingApprovalCard';
import DelegationsList from './components/DelegationsList';
import BiometricSecurityDisclaimer from '../../components/navigation/BiometricSecurityDisclaimer';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const ApprovalManagement = () => {
  const [activeTab, setActiveTab] = useState('chains');
  const [chains, setChains] = useState([]);
  const [selectedChain, setSelectedChain] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChainDetails, setShowChainDetails] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      if (activeTab === 'chains') {
        const chainsData = await approvalWorkflowService?.getAllChainTemplates();
        setChains(chainsData);
      } else if (activeTab === 'pending') {
        const pendingData = await approvalWorkflowService?.getPendingApprovals();
        setPendingApprovals(pendingData);
      } else if (activeTab === 'delegations') {
        const delegationsData = await approvalWorkflowService?.getActiveDelegations();
        setDelegations(delegationsData);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load approval data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleViewChain = async (chainId) => {
    try {
      const chainData = await approvalWorkflowService?.getChainTemplateWithSteps(chainId);
      setSelectedChain(chainData);
      setShowChainDetails(true);
    } catch (err) {
      setError(err?.message || 'Failed to load chain details');
    }
  };

  const handleApprove = async (approval) => {
    try {
      await approvalWorkflowService?.approveStep(
        approval?.id,
        'admin@example.com',
        'Approved via approval management'
      );
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to approve');
    }
  };

  const handleReject = async (approval) => {
    try {
      const reason = prompt('Enter rejection reason:');
      if (reason) {
        await approvalWorkflowService?.rejectStep(
          approval?.id,
          'admin@example.com',
          reason
        );
        loadData();
      }
    } catch (err) {
      setError(err?.message || 'Failed to reject');
    }
  };

  const handleDelegate = async (approval) => {
    try {
      const delegateToEmail = prompt('Enter email to delegate to:');
      if (delegateToEmail) {
        const reason = prompt('Enter delegation reason:');
        const validUntil = new Date();
        validUntil?.setDate(validUntil?.getDate() + 7);
        
        await approvalWorkflowService?.delegateApproval(
          approval?.id,
          approval?.assignedToEmail,
          delegateToEmail,
          reason,
          validUntil?.toISOString()
        );
        loadData();
      }
    } catch (err) {
      setError(err?.message || 'Failed to delegate');
    }
  };

  const handleRevokeDelegation = async (delegationId) => {
    try {
      await approvalWorkflowService?.revokeDelegation(delegationId);
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to revoke delegation');
    }
  };

  const tabs = [
    { id: 'chains', label: 'Approval Chains', icon: 'GitBranch' },
    { id: 'pending', label: 'Pending Approvals', icon: 'Clock' },
    { id: 'delegations', label: 'Delegations', icon: 'UserPlus' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <BiometricSecurityDisclaimer />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />
      
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
            Approval Management
          </h1>
          <p className="caption text-sm md:text-base text-muted-foreground">
            Multi-level approval chains with delegation, escalation rules, and automatic routing
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-error font-medium">Error</p>
              <p className="text-sm text-error">{error}</p>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center gap-2 border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => {
                  setActiveTab(tab?.id);
                  setShowChainDetails(false);
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Icon name="Loader" size={32} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading approval data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'chains' && (
              <>
                {showChainDetails && selectedChain ? (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ArrowLeft"
                      onClick={() => setShowChainDetails(false)}
                      className="mb-6"
                    >
                      Back to Chains
                    </Button>
                    <div className="bg-card rounded-lg border border-border p-6 mb-6">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-2xl font-bold text-foreground mb-2">
                            {selectedChain?.templateName}
                          </h2>
                          <p className="text-muted-foreground">{selectedChain?.description}</p>
                        </div>
                        {selectedChain?.isDefault && (
                          <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded">
                            Default Chain
                          </span>
                        )}
                      </div>
                      
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="GitBranch" size={20} />
                          Approval Steps
                        </h3>
                        <ApprovalStepsList steps={selectedChain?.steps} />
                      </div>
                      
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="Route" size={20} />
                          Routing Rules
                        </h3>
                        <RoutingRulesTable rules={selectedChain?.routingRules} />
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="AlertTriangle" size={20} />
                          Escalation Rules
                        </h3>
                        <EscalationRulesTable rules={selectedChain?.escalationRules} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {chains?.map((chain) => (
                      <ApprovalChainCard
                        key={chain?.id}
                        chain={chain}
                        onView={handleViewChain}
                        onEdit={() => {}}
                      />
                    ))}
                    {chains?.length === 0 && (
                      <div className="col-span-2 text-center py-12 text-muted-foreground">
                        <Icon name="GitBranch" size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No approval chains configured</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {activeTab === 'pending' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pendingApprovals?.map((approval) => (
                  <PendingApprovalCard
                    key={approval?.id}
                    approval={approval}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onDelegate={handleDelegate}
                  />
                ))}
                {pendingApprovals?.length === 0 && (
                  <div className="col-span-2 text-center py-12 text-muted-foreground">
                    <Icon name="CheckCircle" size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No pending approvals</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'delegations' && (
              <div className="max-w-4xl">
                <DelegationsList
                  delegations={delegations}
                  onRevoke={handleRevokeDelegation}
                />
              </div>
            )}
          </>
        )}
      </main>

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default ApprovalManagement;